import { invalidateProjectFinancials } from "@/lib/invalidateProjectFinancials";
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "./StatusBadge";
import { handleRpcError } from "@/lib/rpcErrors";
import { exportPdf } from "@/lib/export";
import { InvoicePrintTemplate } from "@/components/shared/print-templates/InvoicePrintTemplate";
import { Printer, CheckCircle, DollarSign, FileText, Trash2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  invoiceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMutate: () => void;
}

export const InvoiceDetailDrawer = ({ invoiceId, open, onOpenChange, onMutate }: Props) => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assignCustomerId, setAssignCustomerId] = useState("");
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [assignProjectId, setAssignProjectId] = useState("");
  const [reassignOpen, setReassignOpen] = useState(false);

  // Reset all transient form state whenever the drawer is pointed at a different invoice.
  // Without this, fields bleed across invoices because the component instance is reused.
  useEffect(() => {
    setShowPayment(false);
    setPaymentAmount("");
    setPaymentMethod("");
    setPaymentRef("");
    setDeleteConfirmOpen(false);
    setBusy(false);
    setAssignCustomerId("");
    setAssignProjectId("");
    setReassignOpen(false);
    setShowAllProjects(false);
  }, [invoiceId]);


  const { data: invoice, isLoading } = useQuery({
    queryKey: ["invoice-detail", invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*, customers(name), projects(name)")
        .eq("id", invoiceId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!invoiceId && open,
  });

  const { data: lines, error: linesError, isFetching: isLinesFetching } = useQuery({
    queryKey: ["invoice-lines", invoiceId, open],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoice_lines")
        .select("id, description, quantity, unit_price, line_total")
        .eq("invoice_id", invoiceId!)
        .is("deleted_at", null)
        .order("created_at");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!invoiceId && open,
    refetchOnMount: "always",
  });

  const needsAssignment = !!invoice && ((invoice as any).needs_customer_assignment || (invoice as any).needs_project_assignment);
  const isQbImport = (invoice as any)?.source === "qb_import";

  const { data: customers } = useQuery({
    queryKey: ["customers-list-for-assignment"],
    queryFn: async () => {
      const { data } = await supabase.from("customers").select("id, name").order("name");
      return data ?? [];
    },
    enabled: open && (needsAssignment || reassignOpen),
  });

  const { data: assignProjects, isLoading: projectsLoading, error: projectsError } = useQuery({
    queryKey: ["projects-list-for-assignment", assignCustomerId || invoice?.customer_id, showAllProjects],
    queryFn: async () => {
      const custId = assignCustomerId || invoice?.customer_id;
      let q = supabase.from("projects").select("id, name").order("name");
      if (custId && !showAllProjects) q = (q as any).eq("customer_id", custId);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
    enabled: open && (needsAssignment || reassignOpen),
  });


  const invalidateAll = (extraProjectId?: string | null) => {
    qc.invalidateQueries({ queryKey: ["invoice-detail", invoiceId] });
    qc.invalidateQueries({ queryKey: ["invoice-lines", invoiceId] });
    invalidateProjectFinancials(qc, invoice?.project_id ?? null);
    if (extraProjectId && extraProjectId !== invoice?.project_id) {
      invalidateProjectFinancials(qc, extraProjectId);
    }
    onMutate();
  };

  const saveAssignment = async () => {
    if (!invoice) return;
    setBusy(true);
    try {
      const updates: Record<string, any> = {};
      if (assignCustomerId) { updates.customer_id = assignCustomerId; updates.needs_customer_assignment = false; }
      if (assignProjectId) { updates.project_id = assignProjectId; updates.needs_project_assignment = false; }
      if (Object.keys(updates).length === 0) return;
      const previousProjectId = invoice.project_id ?? null;
      const { error } = await supabase.from("invoices").update(updates).eq("id", invoice.id);
      if (error) throw error;
      // Best-effort audit log; ignore failures
      try {
        await supabase.from("ops_audit_log").insert({
          company_id: (invoice as any).company_id,
          table_name: "invoices",
          record_id: invoice.id,
          action: "invoice_reassigned",
          old_status: previousProjectId,
          new_status: assignProjectId || previousProjectId,
          metadata: {
            previous_customer_id: invoice.customer_id ?? null,
            new_customer_id: assignCustomerId || invoice.customer_id,
            previous_project_id: previousProjectId,
            new_project_id: assignProjectId || previousProjectId,
            invoice_number: invoice.invoice_number,
          },
        } as any);
      } catch {}
      toast.success("Invoice updated");
      setAssignCustomerId("");
      setAssignProjectId("");
      setReassignOpen(false);
      invalidateAll(previousProjectId);
    } catch (err) { handleRpcError(err); } finally { setBusy(false); }
  };

  const unassign = async (scope: "project" | "all") => {
    if (!invoice) return;
    const msg =
      scope === "project"
        ? "Clear the project from this invoice? It will return to Needs Review under the same customer."
        : "Clear customer and project from this invoice? It will return to the Needs Review queue.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const updates: Record<string, any> = {
        project_id: null,
        needs_project_assignment: true,
      };
      if (scope === "all") {
        updates.customer_id = null;
        updates.needs_customer_assignment = true;
      }
      const previousProjectId = invoice.project_id ?? null;
      const previousCustomerId = invoice.customer_id ?? null;
      const { error } = await supabase.from("invoices").update(updates).eq("id", invoice.id);
      if (error) throw error;
      try {
        await supabase.from("ops_audit_log").insert({
          company_id: (invoice as any).company_id,
          table_name: "invoices",
          record_id: invoice.id,
          action: "invoice_unassigned",
          old_status: scope === "all" ? "customer+project" : previousProjectId,
          new_status: null,
          metadata: {
            scope,
            previous_customer_id: previousCustomerId,
            previous_project_id: previousProjectId,
            invoice_number: invoice.invoice_number,
          },
        } as any);
      } catch {}
      toast.success(scope === "all" ? "Customer and project cleared" : "Project cleared");
      setAssignCustomerId("");
      setAssignProjectId("");
      setReassignOpen(false);
      invalidateAll(previousProjectId);
    } catch (err) { handleRpcError(err); } finally { setBusy(false); }
  };




  const approve = async () => {
    if (!invoice) return;
    setBusy(true);
    try {
      const { error } = await supabase.rpc("approve_invoice", { p_invoice_id: invoice.id });
      if (error) throw error;
      toast.success("Invoice approved");
      invalidateAll();
    } catch (err) { handleRpcError(err); } finally { setBusy(false); }
  };

  const recordPayment = async () => {
    if (!invoice) return;
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc("transition_invoice_status", {
        p_invoice_id: invoice.id,
        p_new_status: "paid",
        p_payment_amount: amount,
        p_payment_ref: paymentRef || null,
        p_payment_method: paymentMethod || null,
      });
      if (error) throw error;
      const newStatus = (data as any)?.new_status;
      toast.success(newStatus === "paid" ? "Invoice paid in full" : "Partial payment recorded");
      setShowPayment(false);
      setPaymentAmount("");
      setPaymentRef("");
      setPaymentMethod("");
      invalidateAll();
    } catch (err) { handleRpcError(err); } finally { setBusy(false); }
  };

  const deleteInvoice = async () => {
    if (!invoice) return;
    setBusy(true);
    try {
      const { error } = await supabase.rpc("hard_delete_invoice", { p_invoice_id: invoice.id });
      if (error) throw error;
      toast.success("Invoice deleted");
      setDeleteConfirmOpen(false);
      onOpenChange(false);
      invalidateAll();
    } catch (err) { handleRpcError(err); } finally { setBusy(false); }
  };

  const printInvoice = () => {
    if (!invoice) return;
    exportPdf(
      <InvoicePrintTemplate data={{
        invoice_number: invoice.invoice_number,
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date,
        status: invoice.status,
        total: invoice.total,
        amount_paid: invoice.amount_paid || 0,
        payment_method: invoice.payment_method,
        payment_reference: invoice.payment_reference,
        customer_name: (invoice.customers as any)?.name,
        project_name: (invoice.projects as any)?.name,
        notes: invoice.notes,
        line_items: (lines ?? []).map((l: any) => ({
          description: l.description, quantity: l.quantity,
          unit_price: l.unit_price, line_total: l.line_total,
        })),
      }} />,
      `Invoice ${invoice.invoice_number}`
    );
  };

  const subtotal = (lines ?? []).reduce((s, l) => s + Number(l.line_total), 0);
  const total = Number(invoice?.total ?? subtotal);
  const rawAmountPaid = Number(invoice?.amount_paid ?? 0);
  const hasConfirmedPaymentState = !!invoice && ["paid", "partially_paid"].includes(invoice.status);
  const amountPaid = hasConfirmedPaymentState ? rawAmountPaid : 0;
  const balance = Math.max(total - amountPaid, 0);

  const canPay = invoice && ["sent", "partially_paid", "overdue", "approved"].includes(invoice.status);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
        {isLoading || !invoice ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">Loading…</div>
        ) : (
          <>
            <SheetHeader className="pb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <SheetTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  <span className="font-mono">{invoice.invoice_number || "—"}</span>
                </SheetTitle>
                <StatusBadge status={invoice.status} />
                {isQbImport && (
                  <Badge variant="outline" className="text-xs font-normal text-blue-600 border-blue-300">QB</Badge>
                )}
                {needsAssignment && (
                  <Badge variant="outline" className="text-xs font-normal text-amber-700 border-amber-400 gap-1">
                    <AlertTriangle className="h-3 w-3" />Needs Assignment
                  </Badge>
                )}
              </div>
            </SheetHeader>

            <div className="space-y-6">
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Invoice Date</span>
                  <p className="font-medium">{invoice.invoice_date ? format(new Date(invoice.invoice_date), "MMM d, yyyy") : "—"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Due Date</span>
                  <p className="font-medium">{invoice.due_date ? format(new Date(invoice.due_date), "MMM d, yyyy") : "—"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Customer</span>
                  <p className="font-medium">{(invoice.customers as any)?.name ?? "—"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Project</span>
                  <p className="font-medium">{(invoice.projects as any)?.name ?? "—"}</p>
                </div>
                {(invoice as any).po_number && (
                  <div>
                    <span className="text-muted-foreground">PO Number</span>
                    <p className="font-medium">{(invoice as any).po_number}</p>
                  </div>
                )}
                {(invoice as any).terms_raw && (
                  <div>
                    <span className="text-muted-foreground">Terms</span>
                    <p className="font-medium">{(invoice as any).terms_raw}</p>
                  </div>
                )}
              </div>

              {/* Bill-To / Ship-To Addresses */}
              {((invoice as any).bill_to_name || (invoice as any).bill_to_company || (invoice as any).bill_to_address || (invoice as any).ship_to_address) && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {(() => {
                    const inv = invoice as any;
                    const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();
                    const name = inv.bill_to_name as string | null;
                    const company = inv.bill_to_company as string | null;
                    const address = inv.bill_to_address as string | null;
                    const seen = new Set<string>();
                    const lines: { text: string; muted: boolean }[] = [];
                    const push = (text: string | null, muted: boolean) => {
                      if (!text) return;
                      const key = norm(text);
                      if (!key || seen.has(key)) return;
                      seen.add(key);
                      lines.push({ text, muted });
                    };
                    push(name, false);
                    push(company, !!name);
                    // Address may be multi-line; split and dedupe each line against name/company
                    if (address) {
                      const addrLines = address.split(/\r?\n/).filter((l) => l.trim().length > 0);
                      const remaining = addrLines.filter((l) => !seen.has(norm(l)));
                      if (remaining.length > 0) {
                        lines.push({ text: remaining.join("\n"), muted: true });
                      }
                    }
                    if (lines.length === 0) return null;
                    return (
                      <div>
                        <span className="text-muted-foreground text-xs uppercase tracking-wide">Bill To</span>
                        {lines.map((l, i) =>
                          i === 0 && !l.muted ? (
                            <p key={i} className="font-medium">{l.text}</p>
                          ) : (
                            <p key={i} className="text-muted-foreground whitespace-pre-line">{l.text}</p>
                          )
                        )}
                      </div>
                    );
                  })()}
                  {(invoice as any).ship_to_address && (
                    <div>
                      <span className="text-muted-foreground text-xs uppercase tracking-wide">Ship To</span>
                      {(invoice as any).ship_to_name && <p className="font-medium">{(invoice as any).ship_to_name}</p>}
                      {(invoice as any).ship_to_address && (
                        <p className="text-muted-foreground whitespace-pre-line">{(invoice as any).ship_to_address}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Source Reference */}
              {invoice.source_estimate_id && (
                <div className="text-sm text-muted-foreground flex items-center gap-1">
                  <Badge variant="outline" className="text-xs">Converted from Estimate</Badge>
                </div>
              )}

              {/* Assignment panel */}
              {needsAssignment && (
                <div className="border border-amber-300 rounded-lg p-4 bg-amber-50 dark:bg-amber-950/20 space-y-3">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    This invoice needs to be assigned before it appears in project financials.
                  </p>
                  {(invoice as any).needs_customer_assignment && (
                    <div className="space-y-1">
                      <Label className="text-xs">Customer</Label>
                      <Select value={assignCustomerId} onValueChange={(v) => { setAssignCustomerId(v); setAssignProjectId(""); }}>
                        <SelectTrigger><SelectValue placeholder="Select customer…" /></SelectTrigger>
                        <SelectContent className="z-[60]">
                          {(customers ?? []).map((c: any) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {(invoice as any).needs_project_assignment && (
                    <div className="space-y-1">
                      <Label className="text-xs">Project</Label>
                      <Select value={assignProjectId} onValueChange={setAssignProjectId}>
                        <SelectTrigger><SelectValue placeholder="Select project…" /></SelectTrigger>
                        <SelectContent className="z-[60]">
                          {projectsLoading ? (
                            <div className="p-2 text-sm text-muted-foreground">Loading projects…</div>
                          ) : projectsError ? (
                            <div className="p-2 text-sm text-destructive">Could not load projects</div>
                          ) : (assignProjects ?? []).length === 0 ? (
                            <div className="p-2 text-sm text-muted-foreground">No projects found</div>
                          ) : (
                            (assignProjects ?? []).map((p: any) => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showAllProjects}
                          onChange={(e) => { setShowAllProjects(e.target.checked); setAssignProjectId(""); }}
                          className="h-3 w-3"
                        />
                        Show all projects (ignore customer filter)
                      </label>
                    </div>
                  )}
                  <Button
                    size="sm"
                    disabled={
                      busy ||
                      ((invoice as any).needs_customer_assignment && !assignCustomerId) ||
                      ((invoice as any).needs_project_assignment && !assignProjectId)
                    }
                    onClick={saveAssignment}
                  >
                    {busy ? "Saving…" : "Save Assignment"}
                  </Button>

                </div>
              )}

              {/* Reassign customer / project (always available for assigned invoices) */}
              {!needsAssignment && invoice && (
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <p className="font-medium">Customer & Project</p>
                      <p className="text-xs text-muted-foreground">
                        {(invoice.customers as any)?.name || "—"} · {(invoice.projects as any)?.name || "No project"}
                      </p>
                    </div>
                    {!reassignOpen && (
                      <div className="flex items-center gap-2">
                        {invoice.project_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => unassign("project")}
                          >
                            Clear project
                          </Button>
                        )}
                        {invoice.customer_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => unassign("all")}
                          >
                            Clear customer & project
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setReassignOpen(true);
                            setAssignCustomerId(invoice.customer_id || "");
                            setAssignProjectId(invoice.project_id || "");
                          }}
                        >
                          Reassign
                        </Button>
                      </div>
                    )}

                  </div>

                  {reassignOpen && (
                    <div className="space-y-3 pt-2 border-t border-border">
                      {invoice.status === "paid" && (
                        <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 rounded-md p-2">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          <span>This invoice is paid. Reassigning will move recorded revenue between projects.</span>
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label className="text-xs">Customer</Label>
                        <Select
                          value={assignCustomerId}
                          onValueChange={(v) => { setAssignCustomerId(v); setAssignProjectId(""); }}
                        >
                          <SelectTrigger><SelectValue placeholder="Select customer…" /></SelectTrigger>
                          <SelectContent className="z-[60]">
                            {(customers ?? []).map((c: any) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Project</Label>
                        <Select value={assignProjectId} onValueChange={setAssignProjectId}>
                          <SelectTrigger><SelectValue placeholder="Select project…" /></SelectTrigger>
                          <SelectContent className="z-[60]">
                            {projectsLoading ? (
                              <div className="p-2 text-sm text-muted-foreground">Loading projects…</div>
                            ) : projectsError ? (
                              <div className="p-2 text-sm text-destructive">Could not load projects</div>
                            ) : (assignProjects ?? []).length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground">No projects found</div>
                            ) : (
                              (assignProjects ?? []).map((p: any) => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showAllProjects}
                            onChange={(e) => { setShowAllProjects(e.target.checked); }}
                            className="h-3 w-3"
                          />
                          Show all projects (ignore customer filter)
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          disabled={
                            busy ||
                            !assignCustomerId ||
                            !assignProjectId ||
                            (assignCustomerId === invoice.customer_id && assignProjectId === invoice.project_id)
                          }
                          onClick={saveAssignment}
                        >
                          {busy ? "Saving…" : "Save Changes"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => {
                            setReassignOpen(false);
                            setAssignCustomerId("");
                            setAssignProjectId("");
                            setShowAllProjects(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Separator />


              {/* Line Items */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Line Items</h3>
                {isLinesFetching ? (
                  <p className="text-sm text-muted-foreground">Loading line items…</p>
                ) : linesError ? (
                  <p className="text-sm text-destructive">Could not load line items: {(linesError as any)?.message || "Unknown error"}</p>
                ) : !lines || lines.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No line items</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right w-16">Qty</TableHead>
                        <TableHead className="text-right w-24">Unit Price</TableHead>
                        <TableHead className="text-right w-24">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lines.map((line) => {
                        const isDescOnly = Number(line.quantity) === 0 && Number(line.line_total) === 0;
                        if (isDescOnly) {
                          return (
                            <TableRow key={line.id} className="bg-muted/30">
                              <TableCell colSpan={4} className="text-sm italic text-muted-foreground">{line.description}</TableCell>
                            </TableRow>
                          );
                        }
                        return (
                          <TableRow key={line.id}>
                            <TableCell className="text-sm">{line.description}</TableCell>
                            <TableCell className="text-right text-sm">{line.quantity}</TableCell>
                            <TableCell className="text-right text-sm">{fmt(Number(line.unit_price))}</TableCell>
                            <TableCell className="text-right text-sm font-medium">{fmt(Number(line.line_total))}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>

              <Separator />

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{fmt((invoice as any).subtotal ?? subtotal)}</span>
                  </div>
                  {Number((invoice as any).tax_amount ?? 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Sales Tax{(invoice as any).tax_rate ? ` (${(invoice as any).tax_rate}%)` : ""}
                      </span>
                      <span>{fmt(Number((invoice as any).tax_amount))}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-muted-foreground">Total</span>
                    <span>{fmt(total)}</span>
                  </div>
                  {amountPaid > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Amount Paid</span>
                      <span className="text-emerald-600">({fmt(amountPaid)})</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-bold pt-1 border-t border-border">
                    <span>Balance Due</span>
                    <span>{fmt(balance)}</span>
                  </div>
                  {(invoice as any).payment_terms && (
                    <div className="flex justify-between text-xs text-muted-foreground pt-1">
                      <span>Terms</span>
                      <span className="uppercase">{String((invoice as any).payment_terms).replace(/_/g, " ")}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {invoice.notes && (
                <>
                  <Separator />
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Notes</h3>
                    <p className="text-sm whitespace-pre-wrap text-muted-foreground">{invoice.notes}</p>
                  </div>
                </>
              )}

              <Separator />

              {/* QB payment note */}
              {isQbImport && (
                <p className="text-xs text-muted-foreground">
                  Payments are recorded in QuickBooks and reflected here on next import.
                </p>
              )}

              {/* Payment Form */}
              {showPayment && (
                <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
                  <h3 className="text-sm font-semibold">Record Payment</h3>
                  {isQbImport && (
                    <p className="text-xs rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 p-2">
                      This invoice was imported from QuickBooks. Record this payment in QuickBooks as well to keep both systems in sync.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Invoice total: {fmt(total)} · Paid to date: {fmt(amountPaid)} · <strong>Remaining: {fmt(balance)}</strong>
                  </p>
                  {!hasConfirmedPaymentState && rawAmountPaid > 0 && (
                    <p className="text-xs text-muted-foreground">This invoice had an imported paid amount, but it has not been recorded in CommandX yet, so it is being treated as unpaid until an actual payment is recorded.</p>
                  )}
                  {balance <= 0 && (
                    <p className="text-xs text-destructive">This invoice is fully paid. No further payment can be recorded.</p>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Amount</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={paymentAmount}
                      max={balance > 0 ? balance : undefined}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                    />
                    {Number(paymentAmount) > 0 && Number(paymentAmount) <= balance && (
                      <p className="text-xs text-muted-foreground">
                        Remaining after this payment: {fmt(balance - Number(paymentAmount))}
                      </p>
                    )}
                    {Number(paymentAmount) > balance && balance > 0 && (
                      <p className="text-xs text-destructive">
                        Amount exceeds remaining balance of {fmt(balance)}.
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Payment Method</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="check">Check</SelectItem>
                        <SelectItem value="ach">ACH</SelectItem>
                        <SelectItem value="wire">Wire</SelectItem>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Reference (optional)</Label>
                    <Input placeholder="Check #, ACH ref…" value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowPayment(false)}>Cancel</Button>
                    <Button
                      size="sm"
                      disabled={busy || balance <= 0 || Number(paymentAmount) <= 0 || Number(paymentAmount) > balance}
                      onClick={recordPayment}
                    >
                      {busy ? "Saving…" : "Record"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Actions Bar */}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={printInvoice}>
                  <Printer className="mr-1 h-4 w-4" />Print / PDF
                </Button>
                {invoice.status === "draft" && (
                  <Button size="sm" disabled={busy} onClick={approve}>
                    <CheckCircle className="mr-1 h-4 w-4" />Approve
                  </Button>
                )}
                {canPay && !showPayment && (
                  <Button size="sm" variant="secondary" onClick={() => {
                    setPaymentAmount(balance > 0 ? balance.toFixed(2) : "");
                    setShowPayment(true);
                  }}>
                    <DollarSign className="mr-1 h-4 w-4" />Record Payment
                  </Button>
                )}
                {invoice.status !== "paid" && (
                  <Button size="sm" variant="destructive" onClick={() => setDeleteConfirmOpen(true)}>
                    <Trash2 className="mr-1 h-4 w-4" />Delete
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete invoice {invoice?.invoice_number}? This will void the invoice and remove it from active views.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={busy} onClick={deleteInvoice}>
              {busy ? "Deleting…" : "Delete Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
};
